import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import { Close, PictureAsPdf, Download, Image, InsertPhoto, TableChart } from "@mui/icons-material";
import PropTypes from "prop-types";
import { readExcelPreview, MAX_PREVIEW_ROWS, MAX_PREVIEW_COLUMNS } from "./excelPreview";
import ExcelPreviewTable from "./ExcelPreviewTable";
import {
  getConciliationFilePreview,
  getConciliationFilesById,
} from "modules/finops/conciliaciones/services/financieraService";

const IMAGE_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".bmp",
  ".webp",
  ".svg",
  ".tiff",
  ".tif",
];

const EXCEL_EXTENSIONS = [".xlsx", ".xls", ".csv"];

// ================================================
// Helpers
// ================================================
const isExcelFile = (file) => {
  if (!file) return false;

  if (file.isExcel) return true;

  const filename = file.filename || file.file?.originalname || "";
  const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
  if (EXCEL_EXTENSIONS.includes(ext)) return true;

  const mimetype = file.mimetype || file.file?.mimetype || "";
  if (
    mimetype === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mimetype === "application/vnd.ms-excel" ||
    mimetype === "text/csv"
  ) {
    return true;
  }

  return false;
};

const ModalPDF = ({ open, onClose, keyConcil, filesData = [], excelFile = null, loadPreview }) => {
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  // 🆕 Excel render
  const [excelPreview, setExcelPreview] = useState(null);
  const [excelSheetName, setExcelSheetName] = useState(null);
  const previewRevision = useRef(0);

  // ================================================
  // Normalizar el Excel como un "archivo virtual"
  // ================================================
  const normalizedExcel = useMemo(() => {
    if (!excelFile) return null;
    return {
      id: excelFile.id || "__excel_financiera__",
      filename: excelFile.originalname || excelFile.filename || "Excel Financiera.xlsx",
      mimetype:
        excelFile.mimetype || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      size: excelFile.size || 0,
      createdAt: excelFile.createdAt || excelFile.created_at || null,
      isExcel: true,
    };
  }, [excelFile]);

  // ================================================
  // Al abrir
  // ================================================
  useEffect(() => {
    if (open) {
      const baseFiles = filesData.length > 0 ? filesData : [];
      const allFiles = normalizedExcel ? [normalizedExcel, ...baseFiles] : baseFiles;

      if (allFiles.length > 0) {
        setFiles(allFiles);
        handleSelectFile(allFiles[0]);
      } else if (keyConcil) {
        fetchFiles();
      }
    } else {
      setFiles([]);
      setSelectedFile(null);
      setFileUrl(null);
      setError(null);
      setLoading(false);
      setLoadingPreview(false);
      setExcelPreview(null);
      setExcelSheetName(null);
    }
    return () => {
      previewRevision.current += 1;
    };
  }, [open, keyConcil, filesData, normalizedExcel, loadPreview]);

  const fetchFiles = async () => {
    if (!keyConcil) {
      setError("No se proporcionó una llave de conciliación");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getConciliationFilesById(keyConcil);

      if (result.success) {
        const allFiles = result.data || [];
        const combined = normalizedExcel ? [normalizedExcel, ...allFiles] : allFiles;

        setFiles(combined);
        if (combined.length > 0) {
          await handleSelectFile(combined[0]);
        } else {
          setError("No se encontraron archivos en esta conciliación");
        }
      } else {
        throw new Error(result.error || "Error al cargar los archivos");
      }
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ================================================
  // Detectar tipo real
  // ================================================
  const getRealFileType = (file) => {
    if (!file) return "unknown";

    if (isExcelFile(file)) return "excel";

    const filename = file.filename || file.file?.originalname || "";
    const extension = filename.toLowerCase().substring(filename.lastIndexOf("."));

    if (extension === ".pdf") return "pdf";
    if (IMAGE_EXTENSIONS.includes(extension)) return "image";

    const mimetype = file.mimetype || file.file?.mimetype || "";
    if (mimetype === "application/pdf") return "pdf";
    if (mimetype.startsWith("image/")) return "image";

    return "unknown";
  };

  const getFileIcon = (file) => {
    const type = getRealFileType(file);
    if (type === "pdf") return <PictureAsPdf sx={{ color: "#e74c3c" }} />;
    if (type === "image") return <Image sx={{ color: "#2980b9" }} />;
    if (type === "excel") return <TableChart sx={{ color: "#1e7145" }} />;
    return <InsertPhoto sx={{ color: "#95a5a6" }} />;
  };

  const getFileTypeLabel = (file) => {
    const type = getRealFileType(file);
    if (type === "pdf") return "PDF";
    if (type === "image") return "Imagen";
    if (type === "excel") return "Excel";
    return "Archivo";
  };

  // ================================================
  // Seleccionar archivo
  // ================================================
  const handleSelectFile = useCallback(
    async (file) => {
      if (!file) return;
      const revision = ++previewRevision.current;

      setSelectedFile(file);
      setFileUrl(null);
      setFileType(null);
      setError(null);
      setExcelPreview(null);
      setExcelSheetName(null);
      setLoadingPreview(true);

      try {
        const isExcel = isExcelFile(file);
        const type = isExcel ? "excel" : getRealFileType(file);

        setFileType(type);

        // ✅ Todos los archivos (PDF, imagen, Excel) usan el mismo endpoint
        const result = await (loadPreview
          ? loadPreview(file.id)
          : getConciliationFilePreview(file.id));
        if (revision !== previewRevision.current) return;

        if (!result.success || !result.data) {
          throw new Error(result.error || "Error al cargar el preview del archivo");
        }

        const url = result.data.url;
        const blob = result.data.blob;

        if (isExcel) {
          // 🆕 Parsear el Excel localmente
          const arrayBuffer = result.data.arrayBuffer || (await blob.arrayBuffer());
          if (revision !== previewRevision.current) return;
          const preview = readExcelPreview(arrayBuffer);
          setExcelPreview(preview);
          setExcelSheetName(preview.sheetName);
        } else {
          // PDF o imagen: usar la URL del blob
          setFileUrl(url);
        }
      } catch (err) {
        if (revision !== previewRevision.current) return;
        console.error("❌ Error cargando preview:", err);
        setError(`Error al cargar el archivo: ${err.message}`);
        setFileUrl(null);
      } finally {
        if (revision === previewRevision.current) setLoadingPreview(false);
      }
    },
    [loadPreview]
  );

  // ================================================
  // Descargar
  // ================================================
  const handleDownload = useCallback(() => {
    if (!fileUrl && fileType !== "excel") return;

    if (fileType === "excel" && selectedFile?.id) {
      // Excel: pedir de nuevo el preview y descargar el blob
      (loadPreview ? loadPreview(selectedFile.id) : getConciliationFilePreview(selectedFile.id))
        .then((result) => {
          if (result.success && result.data?.url) {
            const link = document.createElement("a");
            link.href = result.data.url;
            link.download = selectedFile.filename || "Excel.xlsx";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        })
        .catch((err) => setError(err.message || "No se pudo descargar el archivo"));
      return;
    }

    const link = document.createElement("a");
    link.href = fileUrl;
    const fileName = selectedFile?.filename || selectedFile?.file?.originalname || "documento";
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [fileUrl, selectedFile, fileType, loadPreview]);

  useEffect(() => {
    return () => {
      if (fileUrl && fileType !== "excel") {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [fileUrl, fileType]);

  // ================================================
  // Render
  // ================================================
  const renderFileContent = () => {
    if (loadingPreview) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
            Cargando documento...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={3}>
          <Alert
            severity="error"
            action={
              selectedFile && (
                <Button color="inherit" size="small" onClick={() => handleSelectFile(selectedFile)}>
                  Reintentar
                </Button>
              )
            }
          >
            {error}
          </Alert>
        </Box>
      );
    }

    const type = fileType || getRealFileType(selectedFile);

    // 🆕 Excel
    if (type === "excel") {
      if (!excelPreview) {
        return (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            p={3}
            gap={2}
          >
            <TableChart sx={{ fontSize: 60, color: "#1e7145", opacity: 0.4 }} />
            <Typography variant="h6" color="text.secondary">
              No se pudo previsualizar el Excel
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Puede descargarlo usando el botón de arriba.
            </Typography>
          </Box>
        );
      }

      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "#e8f5e9",
              borderBottom: "1px solid #a5d6a7",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TableChart sx={{ fontSize: 18, color: "#1e7145" }} />
            <Typography variant="caption" sx={{ color: "#1e7145", fontWeight: 600 }}>
              Vista previa local
            </Typography>
            {excelSheetName && (
              <Chip
                label={`Hoja: ${excelSheetName}`}
                size="small"
                sx={{
                  bgcolor: "#fff",
                  color: "#1e7145",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 22,
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 2,
              "& table": {
                borderCollapse: "collapse",
                fontSize: "0.78rem",
                width: "auto",
                minWidth: "100%",
              },
              "& td, & th": {
                border: "1px solid #e2e8f0",
                padding: "4px 8px",
                whiteSpace: "nowrap",
              },
              "& thead td, & thead th": {
                backgroundColor: "#f1f5f9",
                fontWeight: 700,
                position: "sticky",
                top: 0,
                zIndex: 1,
              },
              "& tr:nth-of-type(even)": {
                backgroundColor: "#f8fafc",
              },
            }}
          >
            {excelPreview.truncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                La vista previa muestra hasta {MAX_PREVIEW_ROWS} filas y {MAX_PREVIEW_COLUMNS}{" "}
                columnas. Puede descargar el archivo completo.
              </Alert>
            )}
            {excelPreview.rows.length > 0 ? (
              <ExcelPreviewTable rows={excelPreview.rows} />
            ) : (
              <Typography variant="body2">La hoja no contiene datos.</Typography>
            )}
          </Box>
        </Box>
      );
    }

    // Esperar URL para PDF/imagen
    if (!fileUrl) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="100%"
          p={3}
        >
          <Alert severity="info">
            <Typography variant="body2">
              Selecciona un archivo de la lista para visualizarlo
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (type === "image") {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f5f5f5",
            p: 2,
          }}
        >
          <Box
            component="img"
            src={fileUrl}
            alt={selectedFile?.filename || "Imagen"}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: 1,
            }}
            onError={() => setError("Error al cargar la imagen.")}
          />
        </Box>
      );
    }

    if (type === "pdf") {
      return (
        <iframe
          src={fileUrl}
          title="PDF Preview"
          style={{ width: "100%", height: "100%", border: "none" }}
          onError={() => setError("Error al cargar el PDF")}
        />
      );
    }

    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100%"
        p={3}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">Tipo de archivo no soportado para visualización</Typography>
        </Alert>
        <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>
          Descargar archivo
        </Button>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {selectedFile ? getFileIcon(selectedFile) : <InsertPhoto sx={{ color: "#2980b9" }} />}
          <Typography variant="h6" fontWeight="bold">
            Visualizador de Documentos
          </Typography>
          {keyConcil && (
            <Chip
              label={`Llave: ${keyConcil}`}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: "#2980b915",
                color: "#2980b9",
                fontFamily: "monospace",
              }}
            />
          )}
          {files.length > 0 && (
            <Chip
              label={`${files.length} archivo(s)`}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: "#27ae6015",
                color: "#27ae60",
              }}
            />
          )}
          {selectedFile && (
            <Chip
              label={getFileTypeLabel(selectedFile)}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor:
                  getRealFileType(selectedFile) === "excel" ? "#e8f5e9" : "#f39c1215",
                color: getRealFileType(selectedFile) === "excel" ? "#1e7145" : "#f39c12",
              }}
            />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Descargar">
            <span>
              <IconButton
                aria-label="Descargar archivo"
                onClick={handleDownload}
                disabled={!selectedFile}
                size="large"
              >
                <Download />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton aria-label="Cerrar visualizador" onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              Cargando archivos...
            </Typography>
          </Box>
        ) : error && files.length === 0 ? (
          <Box p={3}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError(null);
                    if (keyConcil) fetchFiles();
                  }}
                >
                  Reintentar
                </Button>
              }
            >
              <Typography variant="body2" fontWeight="bold">
                Error
              </Typography>
              <Typography variant="caption">{error}</Typography>
            </Alert>
          </Box>
        ) : files.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            p={3}
          >
            <InsertPhoto sx={{ fontSize: 80, color: "#e0e0e0", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No hay documentos disponibles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta conciliación no tiene archivos asociados
            </Typography>
          </Box>
        ) : (
          <Grid container sx={{ height: "100%" }}>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                borderRight: 1,
                borderColor: "divider",
                overflow: "auto",
                maxHeight: "100%",
                bgcolor: "#f8f9fa",
              }}
            >
              <Box p={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                  gutterBottom
                >
                  Archivos Disponibles ({files.length})
                </Typography>
                {files.map((file) => {
                  const fileName = file.filename || file.file?.originalname || "Sin nombre";
                  const isSelected = selectedFile?.id === file.id;
                  const fileTypeIcon = getFileIcon(file);
                  const fileTypeLabel = getFileTypeLabel(file);

                  return (
                    <Paper
                      key={file.id}
                      elevation={isSelected ? 4 : 1}
                      sx={{
                        p: 2,
                        mb: 1,
                        cursor: "pointer",
                        bgcolor: isSelected ? "white" : "transparent",
                        border: isSelected ? 2 : 1,
                        borderColor: isSelected ? "#2980b9" : "divider",
                        borderRadius: 2,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: isSelected ? "white" : "#f0f0f0",
                        },
                      }}
                      onClick={() => handleSelectFile(file)}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {fileTypeIcon}
                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant="body2"
                            fontWeight={isSelected ? "bold" : "normal"}
                            noWrap
                          >
                            {fileName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fileTypeLabel}
                            {file.size ? ` • ${(file.size / 1024).toFixed(1)} KB` : ""}
                            {file.file?.size ? ` • ${(file.file.size / 1024).toFixed(1)} KB` : ""}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Chip
                            label="Ver"
                            size="small"
                            sx={{
                              bgcolor: "#2980b9",
                              color: "white",
                              fontSize: "0.6rem",
                            }}
                          />
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={9}
              sx={{
                height: "100%",
                overflow: "hidden",
                position: "relative",
                bgcolor: "#fafafa",
              }}
            >
              {renderFileContent()}
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

ModalPDF.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  keyConcil: PropTypes.string,
  filesData: PropTypes.array,
  excelFile: PropTypes.object,
  loadPreview: PropTypes.func,
};

ModalPDF.defaultProps = {
  keyConcil: null,
  filesData: [],
  excelFile: null,
  loadPreview: null,
};

export default ModalPDF;
